import {
  ActivityIcon,
  AwardIcon,
  ClipboardCheckIcon,
  ClipboardListIcon,
  ClockIcon,
  FolderIcon,
  HeartPulseIcon,
  LayoutListIcon,
  ScissorsIcon,
  ShieldCheckIcon,
  StethoscopeIcon,
  TargetIcon,
  TimerIcon,
} from 'lucide-react'

type LucideIcon = typeof ShieldCheckIcon

export interface IconItem {
  icon: LucideIcon
  title: string
  description: string
}

export const painPoints: IconItem[] = [
  {
    icon: FolderIcon,
    title: 'Scattered Material',
    description: 'Random notes, old PDFs, and outdated manuals. Every class, you start over.',
  },
  {
    icon: TimerIcon,
    title: 'Preparation Burnout',
    description:
      'Preparing a single lecture consumes time you should be using for your clinical practice or professional growth.',
  },
  {
    icon: ActivityIcon,
    title: 'Lack of Real Scenarios',
    description:
      "You explain concepts, but you don't have updated clinical scenarios to make your students think like real techs.",
  },
  {
    icon: TargetIcon,
    title: 'Low Engagement',
    description:
      "Students disconnect because the material doesn't reflect 2026 protocols or the high-stakes reality of the modern OR.",
  },
]

export interface BenefitItem {
  icon: LucideIcon
  title: string
  description: string
}

export const benefits: BenefitItem[] = [
  {
    icon: ClockIcon,
    title: 'Save Hundreds of Preparation Hours',
    description:
      'Stop building every lesson from zero. Use sequences and cases ready to adapt to your institution.',
  },
  {
    icon: LayoutListIcon,
    title: 'Teach with Pedagogical Order',
    description:
      'Organize your entire semester with annual planning, didactic units, and clear rubrics.',
  },
  {
    icon: StethoscopeIcon,
    title: 'Develop Clinical Judgment',
    description:
      'Apply real surgical situations to help your students think critically under pressure.',
  },
  {
    icon: AwardIcon,
    title: 'Elevate Your Professional Authority',
    description:
      'Deliver solid, updated, and high-level material that increases the perceived value of your program.',
  },
  {
    icon: ClipboardCheckIcon,
    title: 'Close Every Cycle with Control',
    description:
      'Know exactly what to teach, how to evaluate it, and what outcomes you are achieving.',
  },
]

export interface Volume {
  icon: LucideIcon
  number: number
  title: string
  description: string
}

export const volumes: Volume[] = [
  {
    icon: ShieldCheckIcon,
    number: 1,
    title: 'Foundations & Perioperative Safety',
    description:
      'Full surgical technology core: roles, biosecurity, asepsis, and the "Surgical Conscience" framework.',
  },
  {
    icon: ScissorsIcon,
    number: 2,
    title: 'The Modern Surgical Instrument Suite',
    description:
      'Classification, identification, functional logic, and maintenance of all surgical instrumentation.',
  },
  {
    icon: HeartPulseIcon,
    number: 3,
    title: 'Surgical Specialties & Advanced Protocols',
    description:
      'Specialized instruments and workflows: General, Ortho, GYN, Cardiac, Neuro, and MIS.',
  },
  {
    icon: ClipboardCheckIcon,
    number: 4,
    title: 'OR Control, Counting & Safety Systems',
    description: 'Surgical Checklist (WHO), counts, traceability, and adverse event management.',
  },
  {
    icon: StethoscopeIcon,
    number: 5,
    title: 'Clinical Simulation & Case Mastery',
    description:
      '25+ solved clinical scenarios and simulations to develop real-world surgical criteria.',
  },
  {
    icon: ClipboardListIcon,
    number: 6,
    title: 'Faculty Planning Suite 2026',
    description: 'Curriculum mapping, rubrics, assessments, and pedagogical sequences ready to use.',
  },
]

export interface Bonus {
  number: number
  title: string
  description: string
  image: string
}

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: '72-Hour Faculty Launch Blueprint',
    description: 'Turn the 6 volumes into organized, real-world lessons in just three days.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/8f307971-3194-4f35-bb73-da4941411ed4.jpg',
  },
  {
    number: 2,
    title: 'First Week Command Script',
    description:
      'Your first 5 days of class complete with openings, explanations, activities, and closings.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/af32c630-0be6-4385-a327-249b464dbb36.jpg',
  },
  {
    number: 3,
    title: 'Sterile Field Daily Drill Book',
    description: '10-minute repeatable exercises to build sterile discipline habits in your students.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/1ff6d565-c456-4ab6-8743-594786bde293.jpg',
  },
  {
    number: 4,
    title: 'Clinical Debriefing Cards',
    description: '50+ powerful prompts to turn every simulation or mistake into a deep learning moment.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/d0bafd30-3499-42d4-a65f-c37940a13183.jpg',
  },
  {
    number: 5,
    title: 'Curriculum Confidence Audit',
    description:
      'Diagnostic tool to evaluate your current program and create a precision improvement plan.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/5be81d74-7d68-4df4-8a3c-f385914ef5c0.jpg',
  },
]

export const offerIncludes: string[] = [
  'Vol 1: Foundations & Perioperative Safety',
  'Vol 2: The Modern Surgical Instrument Suite',
  'Vol 3: Surgical Specialties & Advanced Protocols',
  'Vol 4: OR Control, Counting & Safety Systems',
  'Vol 5: Clinical Simulation & Case Mastery',
  'Vol 6: Faculty Planning Suite 2026',
  'All 5 Bonuses at no extra cost',
  'One-time payment. Instant access today',
]

export interface Testimonial {
  quote: string
  initials: string
  name: string
  role: string
}

export const testimonials: Testimonial[] = [
  {
    quote:
      'I used to spend weekends building lesson plans. Now I have my entire semester mapped out with 2026 protocols and integrated cases.',
    initials: 'SJ',
    name: 'Sarah J.',
    role: 'Lead Instructor — Houston, TX',
  },
  {
    quote:
      'The Simulation scenarios allowed me to teach the WHO Checklist with real clinical context. My students finally understand their responsibility.',
    initials: 'JR',
    name: 'James R.',
    role: 'Program Director — London, UK',
  },
]

export interface FaqItem {
  question: string
  answer: string
}

export const faqs: FaqItem[] = [
  {
    question: 'Is this physical or digital?',
    answer: '100% Digital (PDF). You get instant access to download and use on any device.',
  },
  {
    question: 'Does it follow AST or International standards?',
    answer:
      'Yes, the system is designed based on 2026 global standards, easily adaptable to ARC/STSA or NHS requirements.',
  },
  {
    question: 'Do I need advanced experience?',
    answer:
      "No. It's designed both for new instructors coming from the OR and veterans looking to modernize their material.",
  },
]

export const HERO_IMAGE =
  'https://cdn.magicpatterns.com/uploads/sZSxb5GagPtBh971UhXYoD/ChatGPT_Image_28_ago_2026,_17_54_16-Photoroom.webp'
